import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { DiscriminatedTreatmentCheckInSubmittedEvent } from '@/services/api/generated/matterChangesService';

import {
  ActivityActions,
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  CollapsibleList,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { formatEnumValue } from '../activity-feed-v2/utils';

export interface TreatmentCheckInSubmittedEventProps {
  event: DiscriminatedTreatmentCheckInSubmittedEvent;
}

interface CheckInDetailField {
  label: string;
  value: string | number | null | undefined;
}

export const TreatmentCheckInSubmittedEvent: React.FC<
  TreatmentCheckInSubmittedEventProps
> = ({ event }) => {
  const { check_in } = event.payload;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-100">
          <ClipboardDocumentCheckIcon className="h-4 w-4 text-teal-600" />
        </div>
      }
    />
  );

  const checkInDetailFields: CheckInDetailField[] = [
    {
      label: 'Status',
      value: check_in?.status ? formatEnumValue(check_in.status) : null,
    },
    { label: 'General Notes', value: check_in?.general_notes },
    {
      label: 'Check-In Time',
      value: check_in?.check_in_time
        ? new Date(check_in.check_in_time).toLocaleString()
        : null,
    },
  ];

  // Add provider updates if they exist
  if (check_in?.provider_updates && check_in.provider_updates.length > 0) {
    check_in.provider_updates.forEach((update, index) => {
      const providerLabel = update.provider_name
        ? `Provider: ${update.provider_name}`
        : `Provider Update ${index + 1}`;
      checkInDetailFields.push({ label: providerLabel, value: '' });

      if (update.new_treatment_status) {
        checkInDetailFields.push({
          label: '  Treatment Status',
          value: formatEnumValue(update.new_treatment_status),
        });
      }
      if (update.new_treatment_frequency) {
        checkInDetailFields.push({
          label: '  Treatment Frequency',
          value: formatEnumValue(update.new_treatment_frequency),
        });
      }
      if (update.new_treatment_progress) {
        checkInDetailFields.push({
          label: '  Treatment Progress',
          value: formatEnumValue(update.new_treatment_progress),
        });
      }
      if (
        update.new_pain_level !== null &&
        update.new_pain_level !== undefined
      ) {
        checkInDetailFields.push({
          label: '  Pain Level',
          value: update.new_pain_level,
        });
      }
      if (update.new_treatment_notes) {
        checkInDetailFields.push({
          label: '  Treatment Notes',
          value: update.new_treatment_notes,
        });
      }
    });
  }

  const detailItems = checkInDetailFields
    .filter(
      ({ value }) => value !== null && value !== undefined && value !== '',
    )
    .map(({ label, value }) => (value ? `${label}: ${value}` : label));

  const body = (
    <ActivityBody>
      <ActivityDescription>
        Urgency: {formatEnumValue(check_in.urgency_level)}
      </ActivityDescription>
      {detailItems.length > 0 && (
        <CollapsibleList
          items={detailItems}
          showLabel="Show Details"
          hideLabel="Hide Details"
        />
      )}
    </ActivityBody>
  );

  const actions = (
    <ActivityActions>
      <ActivityLink href={`/matters/${event.matter_id}/treatment`}>
        View Treatment
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Treatment Check-In Submitted"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
